<template>
  <button
    type="button"
    title="Refresh"
    @click="attemptToRefreshChart()"
    :disabled="isSymbolBeingViewedNotFound || isSymbolBeingViewedAnEODSymbolOnAnIntradayChart"
  ><i
    class="fas fa-fw fa-sync-alt"
    :class="{
      'chart-refresh-button-icon-rotated': isChartRefreshButtonIconRotated,
      'chart-refresh-button-icon-css-transition-enabled': isChartRefreshButtonIconCSSTransitionEnabled
    }"
    @transitionrun="chartRefreshButtonIconTransitionRunCallback()"
    @transitionstart="chartRefreshButtonIconTransitionStartCallback()"
    @transitionend="chartRefreshButtonIconTransitionEndCallback()"
    @transitioncancel="chartRefreshButtonIconTransitionEndCallback()"
  ></i></button>
</template>

<script lane="ts" setup>
  import { ref, watch, nextTick } from 'vue';
  import { storeToRefs } from 'pinia';
  import { useLayoutStore } from '@/stores/layoutStore';
  import { useAutoRefresh } from '@/composables/useAutoRefresh';
  import { useSymbolInformation } from '@/composables/useSymbolInformation';
  import { waitForDomToRepaint } from '@/utils/waitForDomToRepaint';
  import { wait } from '@/utils/wait';

  const layoutStore = useLayoutStore();
  const { chartImageUrlString } = storeToRefs(layoutStore);
  const { refreshChart } = useAutoRefresh();
  const { isSymbolBeingViewedNotFound, isSymbolBeingViewedAnEODSymbolOnAnIntradayChart } = useSymbolInformation();
  const isChartRefreshing = ref(false);
  const isChartRefreshButtonIconCSSTransitionEnabled = ref(true);
  const isChartRefreshButtonIconRotated = ref(false);
  const isChartRefreshButtonIconCSSTransitionOccurring = ref(false);
  const REFRESH_CHART_THROTTLE_MILLISECONDS = 1000;
  let lastChartRefreshButtonIconTransitionRunTime = Date.now();
  let lastChartRefreshButtonIconTransitionStartTime = Date.now();
  let lastChartRefreshButtonIconTransitionEndTime = Date.now();

  async function triggerChartRefreshButtonIconAnimation() {
    if (isChartRefreshing.value) {
      return;
    }

    isChartRefreshing.value = true;
    isChartRefreshButtonIconRotated.value = true;
    isChartRefreshButtonIconCSSTransitionOccurring.value = true;

    await ensureChartRefreshButtonIconTransitionLogicGetsReset();
  }

  async function ensureChartRefreshButtonIconTransitionLogicGetsReset() {
    const lastChartRefreshButtonIconTransitionRunTimeCopy = lastChartRefreshButtonIconTransitionRunTime;
    const lastChartRefreshButtonIconTransitionStartTimeCopy = lastChartRefreshButtonIconTransitionStartTime;
    const lastChartRefreshButtonIconTransitionEndTimeCopy = lastChartRefreshButtonIconTransitionEndTime;

    await nextTick();
    await waitForDomToRepaint();
    await waitForDomToRepaint();
    await wait(0);

    const hasChartRefreshButtonIconTransitionRunEventBeenFired = lastChartRefreshButtonIconTransitionRunTimeCopy !== lastChartRefreshButtonIconTransitionRunTime;
    const hasChartRefreshButtonIconTransitionStartEventBeenFired = lastChartRefreshButtonIconTransitionStartTimeCopy !== lastChartRefreshButtonIconTransitionStartTime;
    const hasChartRefreshButtonIconTransitionEndEventBeenFired = lastChartRefreshButtonIconTransitionEndTimeCopy !== lastChartRefreshButtonIconTransitionEndTime;

    const hasChartRefreshButtonIconTransitionBeenTriggered =
      hasChartRefreshButtonIconTransitionRunEventBeenFired ||
      hasChartRefreshButtonIconTransitionStartEventBeenFired ||
      hasChartRefreshButtonIconTransitionEndEventBeenFired;

    if (hasChartRefreshButtonIconTransitionBeenTriggered) {
      return;
    }

    chartRefreshButtonIconTransitionEndCallback();
  }

  async function chartRefreshButtonIconTransitionRunCallback() {
    lastChartRefreshButtonIconTransitionRunTime = Date.now();
  }

  async function chartRefreshButtonIconTransitionStartCallback() {
    lastChartRefreshButtonIconTransitionStartTime = Date.now();
  }

  async function chartRefreshButtonIconTransitionEndCallback() {
    lastChartRefreshButtonIconTransitionEndTime = Date.now()

    if (!isChartRefreshButtonIconCSSTransitionOccurring.value) {
      return;
    }

    isChartRefreshButtonIconCSSTransitionOccurring.value = false;
    isChartRefreshButtonIconCSSTransitionEnabled.value = false;

    await waitForDomToRepaint();

    isChartRefreshButtonIconRotated.value = false;

    await waitForDomToRepaint();

    isChartRefreshButtonIconCSSTransitionEnabled.value = true;

    await waitForDomToRepaint();

    window.setTimeout(() => {
      isChartRefreshing.value = false;
    }, REFRESH_CHART_THROTTLE_MILLISECONDS);
  }

  function attemptToRefreshChart() {
    if (isChartRefreshing.value) {
      return;
    }

    refreshChart();
  }

  watch(chartImageUrlString, triggerChartRefreshButtonIconAnimation);
</script>

<style scoped>
  i {
    transform: rotate(0deg);
  }

  i.chart-refresh-button-icon-css-transition-enabled {
    transition: transform 1s linear;
  }

  i.chart-refresh-button-icon-rotated {
    transform: rotate(360deg);
  }
</style>
